import { X, Bell, MapPin, Settings, HelpCircle, LogOut, Package, MessageCircle, User, HandshakeIcon, ChevronDown, ChevronUp, Store, ShoppingBag, Wallet } from "lucide-react";
import { useState } from "react";

interface MenuDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  isLoggedIn?: boolean;
  userName?: string;
  userEmail?: string;
  userInitials?: string;
  /** Profile photo URL (http(s)); when set, shown instead of initials in the menu header */
  userProfileImageUrl?: string;
  onLoginClick?: () => void;
  onLogout?: () => void;
  onNavigateToNotifications?: () => void;
  onNavigateToAddresses?: () => void;
  onNavigateToMyAds?: () => void;
  onNavigateToChat?: () => void;
  onNavigateToProfile?: () => void;
  onNavigateToSettings?: () => void;
  onNavigateToHelp?: () => void;
  onNavigateToReceivedOffers?: () => void;
  onNavigateToSentOffers?: () => void;
  onNavigateToSales?: () => void;
  onNavigateToPurchases?: () => void;
  onNavigateToWallet?: () => void;
}

export function MenuDrawer({ isOpen, onClose, isLoggedIn = true, userName, userEmail, userInitials, userProfileImageUrl, onLoginClick, onLogout, onNavigateToNotifications, onNavigateToAddresses, onNavigateToMyAds, onNavigateToChat, onNavigateToProfile, onNavigateToSettings, onNavigateToHelp, onNavigateToReceivedOffers, onNavigateToSentOffers, onNavigateToSales, onNavigateToPurchases, onNavigateToWallet }: MenuDrawerProps) {
  const [showOffers, setShowOffers] = useState(false);
  const [showSales, setShowSales] = useState(false);
  const [showPurchases, setShowPurchases] = useState(false);
  
  const handleNavigate = (action?: () => void) => {
    action?.();
    onClose();
  };

  const handleMenuAction = (action?: () => void) => {
    if (!isLoggedIn) {
      handleNavigate(onLoginClick);
    } else {
      handleNavigate(action);
    }
  };

  const menuItems = [
    { icon: Bell, label: "Notificações", action: () => onNavigateToNotifications?.() },
    { icon: Wallet, label: "Conta Digital", action: () => onNavigateToWallet?.() },
    { icon: Package, label: "Meus Anúncios", action: () => onNavigateToMyAds?.() },
    { icon: MapPin, label: "Meus Endereços", action: () => onNavigateToAddresses?.() },
    { icon: MessageCircle, label: "Chat", action: () => onNavigateToChat?.() },
    { icon: Settings, label: "Configurações", action: () => onNavigateToSettings?.() },
    { icon: HelpCircle, label: "Ajuda & Suporte", action: () => onNavigateToHelp?.() },
  ];

  return (
    <div
      className={`fixed inset-0 z-50 ${!isOpen ? "pointer-events-none" : ""}`}
      aria-hidden={!isOpen}
    >
      <div
        className={`absolute inset-0 bg-black/50 transition-opacity duration-300 ease-out ${
          isOpen ? "opacity-100" : "opacity-0"
        }`}
        onClick={onClose}
      />
      <div
        className={`absolute right-0 top-0 bottom-0 w-80 max-w-[85vw] bg-card shadow-2xl transition-transform duration-300 ease-out ${
          isOpen ? "translate-x-0" : "translate-x-full"
        }`}
      >
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="p-6 border-b border-border flex items-center justify-between">
            <div>
              <h2>Menu</h2>
              <p className="text-sm text-muted-foreground mt-1">Gerencie sua conta</p>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-accent rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* User Info / Guest Login */}
          <div className="p-6 border-b border-border">
            {isLoggedIn ? (
              <>
                <div className="flex items-start gap-4 mb-3">
                  <div className="w-16 h-16 rounded-full overflow-hidden bg-primary flex items-center justify-center text-primary-foreground text-2xl shrink-0">
                    {userProfileImageUrl && (userProfileImageUrl.startsWith("http://") || userProfileImageUrl.startsWith("https://")) ? (
                      <img src={userProfileImageUrl} alt="" className="w-full h-full object-cover" />
                    ) : (
                      (userInitials || "?")
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <h3 className="break-words">{userName || "—"}</h3>
                    <p
                      className="text-sm text-muted-foreground break-all mt-0.5"
                      title={userEmail && userEmail.length > 0 ? userEmail : undefined}
                    >
                      {userEmail || "—"}
                    </p>
                  </div>
                </div>
                <button 
                  onClick={() => handleNavigate(onNavigateToProfile)}
                  className="text-sm text-primary hover:underline"
                >
                  Meu Perfil
                </button>
              </>
            ) : (
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center text-muted-foreground shrink-0">
                  <User className="w-8 h-8" strokeWidth={1.5} />
                </div>
                <button
                  onClick={() => handleNavigate(onLoginClick)}
                  className="text-sm text-primary hover:underline underline underline-offset-2 font-medium"
                >
                  Entrar
                </button>
              </div>
            )}
          </div>

          {/* Menu Items */}
          <div className="flex-1 overflow-y-auto py-2">
            {/* Notifications */}
            <button
              onClick={() => handleMenuAction(onNavigateToNotifications)}
              className="w-full px-6 py-4 flex items-center gap-4 hover:bg-accent transition-colors"
            >
              <Bell className="w-5 h-5 text-muted-foreground" />
              <span>Notificações</span>
            </button>

            {/* Conta Digital */}
            <button
              onClick={() => handleMenuAction(onNavigateToWallet)}
              className="w-full px-6 py-4 flex items-center gap-4 hover:bg-accent transition-colors"
            >
              <Wallet className="w-5 h-5 text-muted-foreground" />
              <span>Conta Digital</span>
            </button>

            {/* Offers Section */}
            <button
              onClick={() => (isLoggedIn ? setShowOffers(!showOffers) : handleNavigate(onLoginClick))}
              className="w-full px-6 py-4 flex items-center gap-4 hover:bg-accent transition-colors"
            >
              <HandshakeIcon className="w-5 h-5 text-muted-foreground" />
              <span className="flex-1 text-left">Negociações</span>
              {showOffers ? <ChevronUp className="w-5 h-5 text-muted-foreground" /> : <ChevronDown className="w-5 h-5 text-muted-foreground" />}
            </button>

            {showOffers && (
              <div className="bg-accent/30">
                <button
                  onClick={() => handleMenuAction(onNavigateToReceivedOffers)}
                  className="w-full pl-16 pr-6 py-3 flex items-center gap-4 hover:bg-accent transition-colors text-sm"
                >
                  <span>Ofertas Recebidas</span>
                </button>
                <button
                  onClick={() => handleMenuAction(onNavigateToSentOffers)}
                  className="w-full pl-16 pr-6 py-3 flex items-center gap-4 hover:bg-accent transition-colors text-sm"
                >
                  <span>Ofertas Enviadas</span>
                </button>
              </div>
            )}

            {/* Minhas Vendas Section */}
            <button
              onClick={() => (isLoggedIn ? setShowSales(!showSales) : handleNavigate(onLoginClick))}
              className="w-full px-6 py-4 flex items-center gap-4 hover:bg-accent transition-colors"
            >
              <Store className="w-5 h-5 text-muted-foreground" />
              <span className="flex-1 text-left">Minhas Vendas</span>
              {showSales ? <ChevronUp className="w-5 h-5 text-muted-foreground" /> : <ChevronDown className="w-5 h-5 text-muted-foreground" />}
            </button>

            {showSales && (
              <div className="bg-accent/30">
                <button
                  onClick={() => handleMenuAction(onNavigateToMyAds)}
                  className="w-full pl-16 pr-6 py-3 flex items-center gap-4 hover:bg-accent transition-colors text-sm"
                >
                  <span>Meus Anúncios</span>
                </button>
                <button
                  onClick={() => handleMenuAction(onNavigateToSales)}
                  className="w-full pl-16 pr-6 py-3 flex items-center gap-4 hover:bg-accent transition-colors text-sm"
                >
                  <span>Vendas</span>
                </button>
              </div>
            )}

            {/* Minhas Compras Section */}
            <button
              onClick={() => (isLoggedIn ? setShowPurchases(!showPurchases) : handleNavigate(onLoginClick))}
              className="w-full px-6 py-4 flex items-center gap-4 hover:bg-accent transition-colors"
            >
              <ShoppingBag className="w-5 h-5 text-muted-foreground" />
              <span className="flex-1 text-left">Minhas Compras</span>
              {showPurchases ? <ChevronUp className="w-5 h-5 text-muted-foreground" /> : <ChevronDown className="w-5 h-5 text-muted-foreground" />}
            </button>

            {showPurchases && (
              <div className="bg-accent/30">
                <button
                  onClick={() => handleMenuAction(onNavigateToPurchases)}
                  className="w-full pl-16 pr-6 py-3 flex items-center gap-4 hover:bg-accent transition-colors text-sm"
                >
                  <span>Compras</span>
                </button>
              </div>
            )}

            {/* Rest of menu items - now starting from Meus Endereços (index 2) */}
            {menuItems.slice(2).map((item, index) => {
              const Icon = item.icon;
              
              return (
                <button
                  key={index}
                  onClick={() => handleMenuAction(item.action)}
                  className="w-full px-6 py-4 flex items-center gap-4 hover:bg-accent transition-colors"
                >
                  <Icon className="w-5 h-5 text-muted-foreground" />
                  <span>{item.label}</span>
                </button>
              );
            })}

            {/* Logout Button (only when logged in) */}
            {isLoggedIn && (
              <button
                onClick={() => onLogout?.()}
                className="w-full px-6 py-4 flex items-center gap-4 hover:bg-accent transition-colors text-destructive"
              >
                <LogOut className="w-5 h-5" />
                <span>Sair</span>
              </button>
            )}
          </div>

          {/* Footer */}
          <div className="p-6 border-t border-border">
            <p className="text-xs text-muted-foreground text-center">
              akihub v1.0.0
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}